// Comentarios sobre un producto del lanzamiento —
// ADR-2026-09-08-comentarios-en-el-producto.
//
// Son "cosas para cambiar en este producto", no una conversación: por eso cada
// uno se puede TACHAR. Una lista de pendientes sin forma de tacharlos solo crece
// hasta que nadie la lee.
//
// Los resueltos no se esconden, se muestran apagados y al final. Esconderlos
// haría que dos personas señalen lo mismo sin enterarse de que ya está hecho.
//
// La CARA es la de los comentarios de Flujos —avatar con la inicial, el nombre y
// el "hace cuánto" arriba del texto, y el compositor con su botón redondo
// adentro— porque son la misma cosa para el usuario y no tenía sentido que se
// vieran como dos productos distintos
// (ADR-2026-09-15-los-comentarios-del-lanzamiento-se-ven-como-los-de-flujos).
// Lo que NO se copia es el modelo: allá un hilo se resuelve entero, acá cada
// comentario se tacha solo, porque esto es una lista de pendientes.
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, Check, Loader2, MessageSquare, RotateCcw, Trash2 } from 'lucide-react';
import { SheetLateral } from '../../components/SheetLateral';
import {
  completarArroba,
  cuando,
  escaparRegex,
  inicialDe,
  menciones,
  sugerenciasArroba,
  type Mencionable,
} from '../../utils/comentariosUi';
import { fetchEquipo } from '../../lib/equipoApi';
// Nota de clases: acá NO se usa `text-brand-black/90` ni `border-black/[0.08]`
// aunque Flujos sí. En este proyecto los colores de marca son variables CSS
// peladas (`var(--brand-black)`), así que Tailwind no les puede componer el
// modificador de opacidad, y la opacidad arbitraria `/[0.xx]` tampoco genera
// regla: la clase no existe y el estilo no se aplica. Verificado contra
// `dist/*.css` — ver el ADR.
import type { ComentarioDeProducto } from './lanzamientosApi';

/**
 * Pinta los @Nombre que corresponden a alguien del equipo. Los que no, quedan
 * como texto: resaltar un "@cualquiercosa" haría creer que a alguien le llegó.
 *
 * El corte es por `split` con UN grupo de captura, así que lo capturado cae
 * siempre en los índices impares. Es a propósito y no un `test` dentro del
 * `map`: una expresión regular con `g` se acuerda de dónde quedó entre llamadas
 * y va alternando verdadero y falso sobre los mismos datos.
 */
function TextoConArrobas({ texto, gente }: { texto: string; gente: readonly Mencionable[] }) {
  const partes = useMemo(() => {
    const nombres = menciones(texto, gente)
      .map((p) => p.nombre)
      // Del más largo al más corto: con "Ana" y "Ana Lucía", la alternancia se
      // queda con la primera que encaja y "@Ana Lucía" se pintaría a medias.
      .sort((a, b) => b.length - a.length);
    if (nombres.length === 0) return null;
    return texto.split(new RegExp(`(@(?:${nombres.map(escaparRegex).join('|')}))`, 'gi'));
  }, [texto, gente]);

  if (!partes) return <>{texto}</>;
  return (
    <>
      {partes.map((parte, i) =>
        i % 2 === 1 ? (
          <span key={i} className="rounded bg-[#E8EEFF] px-1 font-medium text-brand-primary">
            {parte}
          </span>
        ) : (
          <Fragment key={i}>{parte}</Fragment>
        ),
      )}
    </>
  );
}

function Comentario({
  c,
  gente,
  trabajando,
  onResolver,
  onBorrar,
}: {
  c: ComentarioDeProducto;
  gente: readonly Mencionable[];
  trabajando: boolean;
  onResolver: (resuelto: boolean) => void;
  onBorrar: () => void;
}) {
  return (
    <li className="group flex gap-2.5">
      {/* El avatar es lo que hace que la lista se lea como una conversación y no
          como un formulario. Apagado cuando está hecho, igual que el texto. */}
      <span
        aria-hidden="true"
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[15px] font-semibold text-white ${
          c.resuelto ? 'bg-neutral-300' : 'bg-brand-primary'
        }`}
      >
        {inicialDe(c.autor)}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-[15px] font-semibold text-brand-black">{c.autor}</span>
          <span className="shrink-0 text-[13px] text-neutral-400">{cuando(c.createdAt)}</span>
        </div>
        <p
          className={`whitespace-pre-wrap break-words text-[15px] leading-normal ${
            c.resuelto ? 'text-neutral-400 line-through' : 'text-brand-black'
          }`}
        >
          <TextoConArrobas texto={c.texto} gente={gente} />
        </p>
      </div>

      {/* Las acciones van al COSTADO y apagadas, no abajo del texto: una píldora
          verde por comentario convertía la lista en un semáforo y tapaba lo que
          había que leer. Se encienden al pasarles el mouse. Siguen dibujadas
          siempre —no sólo en hover— porque en un teléfono no hay mouse y tachar
          es la acción de esta pantalla. */}
      <div className="flex shrink-0 items-start gap-0.5 pt-0.5">
        <button
          type="button"
          disabled={trabajando}
          onClick={() => onResolver(!c.resuelto)}
          aria-label={c.resuelto ? 'Volver a dejarlo pendiente' : 'Marcar como hecho'}
          title={c.resuelto ? 'Volver a dejarlo pendiente' : 'Marcar como hecho'}
          className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-300 transition-colors hover:bg-[#D1FAE5] hover:text-[#065F46] disabled:opacity-40"
        >
          {c.resuelto ? <RotateCcw className="h-3.5 w-3.5" /> : <Check className="h-4 w-4" />}
        </button>

        {/* Solo los propios: borrar el de otro le saca algo que escribió. La RLS
            lo impide igual; acá ni se ofrece. */}
        {c.mio && (
          <button
            type="button"
            disabled={trabajando}
            onClick={onBorrar}
            aria-label="Borrar mi comentario"
            title="Borrar mi comentario"
            className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-300 transition-colors hover:bg-[#FEE7E7] hover:text-[#B91C1C] disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </li>
  );
}

export default function PanelDeComentarios({
  abierto,
  nombreDelProducto,
  comentarios,
  cargando,
  trabajando,
  error,
  onCerrar,
  onComentar,
  onResolver,
  onBorrar,
}: {
  abierto: boolean;
  nombreDelProducto: string;
  comentarios: ComentarioDeProducto[];
  cargando: boolean;
  trabajando: boolean;
  error: string | null;
  onCerrar: () => void;
  onComentar: (texto: string) => void;
  onResolver: (id: string, resuelto: boolean) => void;
  onBorrar: (id: string) => void;
}) {
  const [texto, setTexto] = useState('');
  const [cursor, setCursor] = useState(0);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);

  /**
   * A quién se puede arrobar: el equipo de la empresa.
   *
   * Se pide cuando el panel se abre por primera vez y no con la pantalla, por la
   * misma razón que los comentarios: es para una función que se usa a veces. Si
   * falla se queda vacío y lo único que se pierde son las sugerencias — escribir
   * el comentario nunca depende de esto.
   */
  const [gente, setGente] = useState<Mencionable[]>([]);
  const equipoPedido = useRef(false);
  useEffect(() => {
    if (!abierto || equipoPedido.current) return;
    equipoPedido.current = true;
    void (async () => {
      try {
        const e = await fetchEquipo();
        setGente(
          e.miembros.map((m) => ({ id: m.id, nombre: (m.nombre || m.email || '').trim() })).filter((m) => m.nombre !== ''),
        );
      } catch {
        /* sin sugerencias, el resto anda igual */
      }
    })();
  }, [abierto]);

  const sugerencias = sugerenciasArroba(texto, cursor, gente);

  /** Pone "@Nombre " en lugar de lo que se venía escribiendo y sigue el cursor. */
  const completar = (nombre: string) => {
    const r = completarArroba(texto, cursor, nombre);
    setTexto(r.texto);
    setCursor(r.cursor);
    requestAnimationFrame(() => {
      areaRef.current?.focus();
      areaRef.current?.setSelectionRange(r.cursor, r.cursor);
    });
  };

  // Al abrir, el cursor ya está donde se escribe: el caso normal es venir a
  // dejar uno, no a leer.
  useEffect(() => {
    if (abierto) areaRef.current?.focus();
  }, [abierto]);

  // La caja arranca en una línea y crece con lo que se escribe, hasta el tope que
  // fija `max-h-40`. Sin esto, el compositor de una línea deja leer un comentario
  // de tres renglones por una mirilla.
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [texto, abierto]);

  const pendientes = comentarios.filter((c) => !c.resuelto);
  const hechos = comentarios.filter((c) => c.resuelto);
  const puede = texto.trim() !== '' && !trabajando;

  const mandar = () => {
    if (!puede) return;
    onComentar(texto.trim());
    setTexto('');
    setCursor(0);
  };

  return (
    <SheetLateral open={abierto} onClose={onCerrar} titulo="Comentarios">
      <div className="space-y-5 p-4">
        <p className="text-[13px] leading-relaxed text-neutral-500">
          Qué hay que cambiar en “{nombreDelProducto}”. Los ve todo tu equipo, y cualquiera
          puede tacharlos cuando ya está hecho.
        </p>

        {/* El compositor de Flujos: una sola caja con el campo y su botón
            redondo adentro. El botón vive ahí y no abajo a la derecha porque es
            el final de la misma cosa que se está escribiendo. */}
        <div className="relative">
          {sugerencias && sugerencias.opciones.length > 0 && (
            <div className="absolute bottom-full left-0 z-10 mb-1 w-[300px] max-w-full rounded-xl border border-neutral-200 bg-white p-1 shadow-lg">
              {sugerencias.opciones.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  // `onMouseDown` con `preventDefault`: sin esto el campo pierde
                  // el foco antes del click y el cursor se va a otro lado.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => completar(p.nombre)}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[15px] text-brand-black hover:bg-surface-elevated"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-primary text-[10px] font-semibold text-white">
                    {inicialDe(p.nombre)}
                  </span>
                  <span className="truncate">{p.nombre}</span>
                </button>
              ))}
            </div>
          )}

          {/* El alto de la caja es el del botón más su margen: 40 del botón y 4
              arriba y abajo. Con el campo más alto que el botón, quedaba un hueco
              arriba y ninguno abajo, y el botón se veía descolgado. */}
          <div className="flex items-end gap-1 rounded-2xl border border-neutral-200 bg-white p-1 transition-colors focus-within:border-brand-primary">
            <textarea
              ref={areaRef}
              rows={1}
              value={texto}
              maxLength={2000}
              placeholder="Ej: falta la foto de atrás, y el precio va con IVA"
              onChange={(e) => {
                setTexto(e.target.value);
                setCursor(e.target.selectionStart ?? e.target.value.length);
              }}
              onSelect={(e) => setCursor((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
              onKeyDown={(e) => {
                // Enter manda y Shift+Enter hace el salto, como en Flujos: con el
                // botón adentro de la caja, la caja se lee como un campo de chat y
                // eso es lo que un chat hace. Con la lista de arrobas abierta,
                // Enter elige la primera, que es lo que se está por hacer.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (sugerencias && sugerencias.opciones.length > 0) {
                    completar(sugerencias.opciones[0]!.nombre);
                  } else {
                    mandar();
                  }
                }
              }}
              className="max-h-40 min-h-[40px] flex-1 resize-none bg-transparent px-3 py-2.5 text-[15px] leading-5 text-brand-black outline-none placeholder:text-neutral-400"
            />
            <button
              type="button"
              disabled={!puede}
              onClick={mandar}
              aria-label="Comentar"
              title="Comentar (Enter)"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-primary text-white transition-opacity disabled:opacity-40"
            >
              {trabajando ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </button>
          </div>
          <p className="mt-2 px-1 text-[13px] text-neutral-400">
            Enter para comentar, Shift+Enter para otra línea.
            {gente.length > 1 && ' Escribí @ para avisarle a alguien del equipo.'}
          </p>
        </div>

        {error && (
          <p className="rounded-xl bg-[#FEE7E7] py-2 px-3 text-[13px] text-[#B91C1C]">{error}</p>
        )}

        {cargando ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-neutral-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : comentarios.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-neutral-300 py-10 px-4 text-center">
            <MessageSquare className="mx-auto h-6 w-6 text-neutral-300" />
            <p className="mt-3 text-[15px] font-semibold text-brand-black">
              Todavía no hay comentarios
            </p>
            <p className="mx-auto mt-1 max-w-xs text-[13px] text-neutral-500">
              Anotá acá lo que haya que corregir y no se te va a perder.
            </p>
          </div>
        ) : (
          <>
            {pendientes.length > 0 && (
              <ul className="space-y-5">
                {pendientes.map((c) => (
                  <Comentario
                    key={c.id}
                    c={c}
                    gente={gente}
                    trabajando={trabajando}
                    onResolver={(v) => onResolver(c.id, v)}
                    onBorrar={() => onBorrar(c.id)}
                  />
                ))}
              </ul>
            )}

            {/* Los hechos van al final y apagados, pero se ven: esconderlos haría
                que dos personas señalen lo mismo sin saber que ya se resolvió. */}
            {hechos.length > 0 && (
              <div>
                <p className="mb-3 text-[13px] font-medium text-neutral-400">
                  Hechos ({hechos.length})
                </p>
                <ul className="space-y-5">
                  {hechos.map((c) => (
                    <Comentario
                      key={c.id}
                      c={c}
                      gente={gente}
                      trabajando={trabajando}
                      onResolver={(v) => onResolver(c.id, v)}
                      onBorrar={() => onBorrar(c.id)}
                    />
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </SheetLateral>
  );
}
